/**
 * My Quotes — Saved / Sent list view.
 *
 * One component drives both `/my-quotes/saved` (status=draft) and
 * `/my-quotes/sent` (status in {sent, accepted, declined}). The page-level
 * route component passes a `mode` prop and we fetch the matching slice via
 * `GET /api/project-quotes?mine=true&status=...`.
 *
 * Each row → tap to open in the builder (`/quote-project?quote=<id>`).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Home, FolderOpen, Send, FilePlus2, Calendar, User, PoundSterling,
  ChevronRight, Mail, CheckCircle2, XCircle, Package,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import API_URL from '@/config/api';

const MODES = {
  saved: {
    title: 'Saved Quotes',
    blurb: 'Drafts you’ve started but not yet sent to the customer.',
    icon: FolderOpen,
    statuses: ['draft'],
    accent: '#D4A24B',
    emptyHint: 'No saved drafts yet — start one and hit Save inside the builder.',
  },
  sent: {
    title: 'Sent Quotes',
    blurb: 'Quotes you’ve already emailed to a customer.',
    icon: Send,
    statuses: ['sent', 'accepted', 'declined'],
    accent: '#1f7a4a',
    emptyHint: 'No sent quotes yet — every quote you email from the builder lands here.',
  },
};

const STATUS_BADGE = {
  draft:    { bg: 'bg-amber-100',   text: 'text-amber-800',   label: 'Draft',    Icon: FolderOpen },
  sent:     { bg: 'bg-blue-100',    text: 'text-blue-800',    label: 'Sent',     Icon: Mail },
  accepted: { bg: 'bg-emerald-100', text: 'text-emerald-800', label: 'Accepted', Icon: CheckCircle2 },
  declined: { bg: 'bg-rose-100',    text: 'text-rose-800',    label: 'Declined', Icon: XCircle },
};

function fmtDate(ts) {
  if (!ts) return '';
  try {
    return new Date(ts).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return '';
  }
}

export default function MyQuotesList({ mode }) {
  const navigate = useNavigate();

  // One tap: draft material price → confirmed Ox order (+ quote marked accepted), then refresh the list.
  const orderMaterials = async (row) => {
    if (!window.confirm(`Order the priced materials for ${row.customer_name || 'this quote'} from Ox Resin? This marks the quote as accepted.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/quotes/${row.material_quote_id}/place-order`, { method: 'POST', credentials: 'include' });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.detail || 'Could not place the order');
      toast.success(`Order ${body.reference} placed with Ox Resin`);
      load();
    } catch (e) { toast.error(e.message); }
  };
  const conf = MODES[mode] || MODES.saved;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Issue one request per status so we can union (draft is one; sent
      // page is the union of sent/accepted/declined). Three or fewer
      // requests is fine — typical list size is small.
      const results = await Promise.all(
        conf.statuses.map((s) =>
          fetch(`${API_URL}/api/project-quotes?mine=true&status=${s}`, { credentials: 'include' })
            .then((r) => (r.ok ? r.json() : []))
        ),
      );
      const merged = results.flat();
      merged.sort((a, b) => (b.updated_at || b.created_at || '').localeCompare(a.updated_at || a.created_at || ''));
      setRows(merged);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [conf.statuses]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!q.trim()) return rows;
    const needle = q.toLowerCase();
    return rows.filter((r) =>
      [r.reference, r.customer_name, r.customer_address, r.customer_email]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(needle))
    );
  }, [rows, q]);

  const Icon = conf.icon;

  return (
    <div className="min-h-screen bg-gray-50">
      <div
        className="text-white"
        style={{
          background: 'linear-gradient(135deg,#0B0B0E 0%,#1f4030 75%)',
          borderBottom: `3px solid ${conf.accent}`,
        }}
      >
        <div className="max-w-[1100px] mx-auto px-3 sm:px-4 py-4 sm:py-5 flex items-center gap-2 sm:gap-3">
          <button
            onClick={() => navigate('/my-quotes')}
            data-testid="mql-back"
            className="p-2 hover:bg-white/10 rounded-lg shrink-0"
            aria-label="Back to Customer Quotes"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-2xl font-bold flex items-center gap-2 truncate">
              <Icon className="w-5 h-5 sm:w-6 sm:h-6 shrink-0" style={{ color: conf.accent }} />
              {conf.title}
            </h1>
            <p className="hidden sm:block text-white/70 text-sm mt-0.5">{conf.blurb}</p>
          </div>
          <Button
            onClick={() => navigate('/')}
            variant="outline"
            size="sm"
            className="bg-white/10 border-white/30 text-white hover:bg-white/20 shrink-0 px-2 sm:px-3"
          >
            <Home className="w-4 h-4 sm:mr-1.5" />
            <span className="hidden sm:inline">Home</span>
          </Button>
        </div>
      </div>

      <div className="max-w-[1100px] mx-auto px-3 sm:px-4 py-5">
        {/* Quick toolbar */}
        <div className="flex flex-wrap gap-2 items-center mb-3">
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by customer, reference or address…"
            data-testid="mql-search"
            className="max-w-sm h-9 text-sm"
          />
          <div className="flex-1" />
          <Button
            onClick={() => navigate('/quote-project')}
            data-testid="mql-new"
            size="sm"
            className="text-white"
            style={{ backgroundColor: '#2F5E96' }}
          >
            <FilePlus2 className="w-4 h-4 mr-1.5" /> Start a new quote
          </Button>
        </div>

        {loading ? (
          <Card className="p-10 text-center text-sm text-gray-500">Loading…</Card>
        ) : filtered.length === 0 ? (
          <Card className="p-10 text-center" data-testid="mql-empty">
            <div
              className="w-14 h-14 mx-auto mb-3 rounded-2xl flex items-center justify-center"
              style={{ background: conf.accent + '22', color: conf.accent }}
            >
              <Icon className="w-7 h-7" />
            </div>
            <p className="text-sm text-gray-500 max-w-md mx-auto">
              {q.trim() ? 'No quotes match that search.' : conf.emptyHint}
            </p>
          </Card>
        ) : (
          <div className="space-y-2" data-testid="mql-list">
            {filtered.map((r) => (
              <Row key={r.id} row={r} onOpen={() => navigate(`/quote-project?quote=${r.id}`)} onMaterial={() => navigate(`/order?quote=${r.id}`)} onOrder={() => orderMaterials(r)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ row, onOpen, onMaterial, onOrder }) {
  const s = STATUS_BADGE[row.status] || STATUS_BADGE.draft;
  const SIcon = s.Icon;
  return (
    <Card
      className="p-3 sm:p-4 flex items-center gap-3 hover:bg-gray-50 cursor-pointer"
      onClick={onOpen}
      data-testid={`mql-row-${row.id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${s.bg} ${s.text}`}>
            <SIcon className="w-2.5 h-2.5" /> {s.label}
          </span>
          <span className="text-xs text-gray-400">{row.reference || '—'}</span>
          {row.revision_number > 1 && (
            <span className="text-[10px] font-bold text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">v{row.revision_number}</span>
          )}
        </div>
        <div className="mt-1 font-bold text-gray-900 text-sm sm:text-base truncate flex items-center gap-1.5">
          <User className="w-3.5 h-3.5 text-gray-400 shrink-0" />
          {row.customer_name || 'Unnamed customer'}
        </div>
        <div className="mt-0.5 text-xs text-gray-500 flex items-center gap-3 flex-wrap">
          {row.customer_address && <span className="truncate max-w-[260px]">{row.customer_address}</span>}
          <span className="inline-flex items-center gap-1">
            <Calendar className="w-3 h-3" /> {fmtDate(row.updated_at || row.created_at)}
          </span>
          {row.total_m2 ? <span>{row.total_m2.toFixed(1)} m²</span> : null}
          {row.material_total != null && (
            <span className={`inline-flex items-center gap-1 font-semibold ${row.material_status && row.material_status !== 'draft' ? 'text-emerald-700' : 'text-[#2F5E96]'}`} data-testid={`mql-material-${row.id}`}>
              <Package className="w-3 h-3" /> Materials £{Number(row.material_total_ex_vat ?? row.material_total).toFixed(2)}{row.material_status && row.material_status !== 'draft' ? ' · ordered' : ''}
            </span>
          )}
        </div>
      </div>
      {row.material_quote_id && row.material_status === 'draft' ? (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onOrder(); }}
          className="hidden sm:inline-flex items-center gap-1 h-8 px-2.5 rounded-lg bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-700 shrink-0"
          title="Place a confirmed order with Ox Resin for the priced materials (marks the quote accepted)"
          data-testid={`mql-order-btn-${row.id}`}
        >
          <Package className="w-3.5 h-3.5" /> Order materials
        </button>
      ) : !(row.material_status && row.material_status !== 'draft') && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onMaterial(); }}
          className="hidden sm:inline-flex items-center gap-1 h-8 px-2.5 rounded-lg border border-[#2F5E96]/40 text-[#2F5E96] text-xs font-bold hover:bg-[#2F5E96]/10 shrink-0"
          title={row.material_quote_id ? 'Re-price the Ox materials for this quote' : 'Price the Ox materials for this quote'}
          data-testid={`mql-material-btn-${row.id}`}
        >
          <Package className="w-3.5 h-3.5" /> {row.material_quote_id ? 'Re-price' : 'Material price'}
        </button>
      )}
      <div className="text-right shrink-0">
        <div className="text-base sm:text-lg font-bold text-gray-900 inline-flex items-center">
          <PoundSterling className="w-4 h-4 text-gray-400" />
          {(row.grand_total || 0).toFixed(2)}
        </div>
        <div className="text-[10px] text-gray-400 uppercase tracking-wide">incl. VAT</div>
      </div>
      <ChevronRight className="w-4 h-4 text-gray-300 shrink-0 hidden sm:block" />
    </Card>
  );
}
